/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
    object, func, string, bool,
} from 'prop-types';
import { withRouter } from 'react-router';
import withStyles from '@material-ui/core/styles/withStyles';
import SearchIcon from '@material-ui/icons/Search';
import { Container, Grid } from '@material-ui/core';
import Media from 'react-media';
import loadable from '@loadable/component';
import { useLazyQuery } from 'react-apollo';
import gql from 'graphql-tag';
import { getFeatureFlags, getDisplayDeliveryMethod } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getSSRDeviceType } from '../../../../state/ducks/App/App-Selectors';
import { GRAPHQL_ENV } from '../../../gql';
import getWikiProducts from './utilities';
import MobileDesktopFacetsV3 from '../GraphqlCategoryPage/Partials/ProductFacetsV3/MobileDesktopFacetsV3';

const PlantsFilterDialog = loadable(() => import(/* webpackChunkName: "FilterDialog" */ '../GraphqlCategoryPage/Partials/FilterDialog/PlantsFilterDialog'));

const styles = (theme) => {
    const { palette, typography, breakpoints: { minWidth, maxWidth } } = theme;
    return {
        plantswikiStyles: {
            '&.plantsWikiWrapper': {
                paddingBottom: '50px',
                paddingTop: '50px',
                [theme.breakpoints.down(maxWidth.small_screen_up)]: {
                    paddingBottom: '20px',
                    paddingTop: '20px',
                },
                '& h1': {
                    fontFamily: typography.secondaryFontFamily,
                    fontSize: '55px',
                    letterSpacing: 'normal',
                    paddingBottom: '10px',
                    margin: '0px',
                    fontWeight: '500',
                    lineHeight: '38px',
                    [theme.breakpoints.up(minWidth.big_screen_wiki_tiles)]: {
                        fontSize: '128px',
                        lineHeight: '50px',
                    },
                },
                '& .searchSection': {
                    [theme.breakpoints.up(minWidth.xxs_screen_up)]: {
                        margin: '10px 0px 10px 0px',
                    },
                    background: palette.primary.main,
                    alignItems: 'center',
                    [theme.breakpoints.down(maxWidth.medium_screen_down)]: {
                        paddingTop: '25px !important',
                    },
                    '& .jumpToSection': {
                        '& span': {
                            marginLeft: '20px',
                        },
                        [theme.breakpoints.down(maxWidth.big_screen_down)]: {
                            padding: '20px 15px 0px 15px',
                            '& span': {
                                marginLeft: '0px',
                            },
                        },
                        '& div': {
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            '& a': {
                                padding: '3px 6px',
                                cursor: 'pointer',
                                color: palette.common.black,
                                textDecoration: 'none',
                                fontWeight: 'bold',
                                '&.alphaDisabled': {
                                    pointerEvents: 'none',
                                    color: palette.common.gray_dark,
                                },
                            },
                        },
                    },
                    '& .inputSection': {
                        position: 'relative',
                        width: '406px',
                        alignItems: 'center',
                        [theme.breakpoints.down(maxWidth.medium_screen_up)]: {
                            padding: '20px',
                            width: '305px',
                            display: 'contents',
                        },
                        '& .searchIcon': {
                            position: 'absolute',
                            margin: '12px',
                        },
                        '& input': {
                            display: 'block',
                            width: '100%',
                            height: '50px',
                            padding: '0.5rem',
                            fontFamily: typography.fontFamily,
                            fontSize: '1rem',
                            fontWeight: 400,
                            lineHeight: 1.25,
                            color: palette.common.gray,
                            backgroundColor: '#fff',
                            backgroundClip: 'padding-box',
                            border: `1px solid ${palette.common.gray}`,
                            borderRadius: '26px',
                            paddingLeft: '12% !important',
                            [theme.breakpoints.between(
                                minWidth.xs_medium_screen_up,
                                maxWidth.medium_screen_down,
                            )]: {
                                paddingLeft: '9% !important',
                            },
                            [theme.breakpoints.between(
                                minWidth.small_screen_up,
                                maxWidth.medium_screen_up,
                            )]: {
                                paddingLeft: '7% !important',
                            },
                            [theme.breakpoints.down(maxWidth.xs_large_screen)]: {
                                paddingLeft: '14% !important',
                            },
                        },
                        [theme.breakpoints.between(
                            minWidth.small_screen_up,
                            maxWidth.medium_screen_down,
                        )]: {
                            width: 'unset !important',
                        },
                    },
                },
            },
            '& .d-block': {
                display: 'block',
            },
            '& .d-flex': {
                display: 'flex',
            },
            '& .d-table': {
                display: 'table',
            },
            '& .d-table-cell': {
                display: 'table-cell',
            },
            '& .w-50': {
                width: '50%',
            },
            '& .justify-content-around': {
                justifyContent: 'space-around !important',
            },
            '& .pb-2': {
                paddingBottom: '0.5rem !important',
            },
            '& .pt-2': {
                paddingTop: '0.5rem !important',
            },
            '& .pt-md-3': {
                [theme.breakpoints.up(maxWidth.small_screen_up)]: {
                    paddingTop: '1rem !important',
                },
            },
            '& .p-3': {
                padding: '1rem !important',
            },
            '& .pt-3': {
                paddingTop: '1rem !important',
            },
            '& .pb-0': {
                paddingBottom: '0 !important',
            },
            '& .m-0': {
                margin: '0 !important',
            },
            '& .p-1': {
                padding: '0.25rem !important',
            },
            '& .p-2': {
                padding: '0.5rem !important',
            },
            '& p-md-4': {
                [theme.breakpoints.up(maxWidth.small_screen_up)]: {
                    padding: '1.5rem !important',
                },
            },
            '& .p-xl-5': {
                [theme.breakpoints.up(maxWidth.big_screen_down)]: {
                    padding: '3rem !important',
                },
            },
            '& .text-center': {
                textAlign: 'center !important',
            },
            '& .wikiTiles': {
                flexWrap: 'wrap',
                [theme.breakpoints.up(minWidth.small_screen_up)]: {
                    justifyContent: 'center !important',
                },
                '& a': {
                    cursor: 'pointer',
                    color: palette.common.black,
                    textDecoration: 'none',
                    fontWeight: 'bold',
                },
                '& .noResults': {
                    width: '100%',
                },
                '& .productTile, .alphabetTile': {
                    paddingBottom: '8px',
                    paddingRight: '8px',
                    paddingLeft: '10px',
                    textAlign: 'center',
                    borderRight: `1px dashed ${palette.common.black}`,
                    borderBottom: `1px dashed ${palette.common.black}`,
                    '&:last-child': {
                        borderRight: 'none',
                    },
                    [theme.breakpoints.up(maxWidth.ipad_pro_screen_down)]: {
                        '&:nth-child(4n)': {
                            borderRight: 'none',
                        },
                        '&:nth-child(-n + 4)': {
                            '& a': {
                                paddingTop: '5px !important',
                            },
                        },
                    },
                    [theme.breakpoints.between(minWidth.wiki_screen_size, maxWidth.ipad_pro_screen_down)]: {
                        '&:nth-child(3n)': {
                            borderRight: 'none',
                        },
                        '&:nth-child(-n + 3)': {
                            '& a': {
                                paddingTop: '5px !important',
                            },
                        },
                    },
                    [theme.breakpoints.down(minWidth.wiki_screen_size)]: {
                        '&:nth-child(2n)': {
                            borderRight: 'none',
                        },
                        '&:nth-child(-n + 2)': {
                            '& a': {
                                paddingTop: '5px !important',
                            },
                        },
                    },
                    '& .productTitle': {
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1rem',
                        color: palette.common.black,
                        textDecoration: 'none',
                        fontWeight: 'normal',
                        paddingBottom: '0px !important',
                        lineHeight: '1.25',
                        letterSpacing: '0px',
                    },
                    '& .alphabet': {
                        fontSize: '14.5625rem',
                        fontWeight: 700,
                        fontFamily: typography.secondaryFontFamily,
                        alignItems: 'center',
                        cursor: 'inherit',
                        [theme.breakpoints.down(maxWidth.small_medium_down)]: {
                            fontSize: '8.688rem',
                        },
                        '&:hover': {
                            textDecoration: 'none',
                        },
                    },
                },
            },
        },
    };
};

const CATEGORY_PRODUCT_QUERY = gql`
query CategoryProductQuery($productOptions: ProductOptions, $brand: String!, $environment: String!, $id: String!, $locale: String!, $zipCode: String!, $locationType: String!) {
    findCategoryById(brand: $brand, environment: $environment, id: $id, locale: $locale, productOptions: $productOptions, zipCode: $zipCode, locationType: $locationType) {
        products {
            id
            brandId
            is3dEnabled
        image {
            additionalImages
            altText
            name
            path
            snipe
            snipeImagePath
            snipeProductEndDate
            snipeCategoryEndDate
            snipeProductStartDate
            snipeCategoryStartDate
            collectionImage
            __typename
        }
        productType
        reviews
        partNumber
        baseCode
        prices {
            currency
            type
            value
            __typename
        }
        skuPriceRange {
            sale {
                value
            }
            retail {
                value
            }
            __typename
        }
        name
        brand
        seo {
            url
            __typename

        }
        productSkus {
            marketplaceShopName
            partNumber
            id
            __typename
          }
        isPassportEligible
        isPersonalizable
        availability {
            availabilityIndicator
            deliveryDateType
            displayEndDate
            displayStartDate
            earliestShipDate
            latestShipDate
            perishable
            productDeliveryType
            shipAlone
            deliveryMessage
            __typename
        }
        __typename
    }
    totalProducts
    __typename
}
}
`;

const PlantGuidePage = (props) => {
    const {
        classes, wikiPageData, history, ssrDeviceType, featureFlags, displayDeliveryMethod, abCategorySortAndFilter, trackEventAction, presentation_family, selectedFacet,
    } = props;
    const { category } = wikiPageData;
    const productFacetsBase = category?.productFacets?.filter((item) => item.name !== 'Price Range');
    const [searchText, setSearchText] = useState();
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [facetsData, setFacetData] = useState([]);
    const productCount = 0;
    const onValueChange = (e) => {
        setSearchText(e.target.value);
    };
    const getProductLink = (slug) => `/plant-guide${slug}`;
    const handleResize = () => {
        setIsSmallScreen(window.innerWidth <= 1024);
    };
    const scrollToAplhabet = (alphabet) => {
        setSearchText('');
        setTimeout(() => {
            const elem = document.querySelector(`#${alphabet}`);
            if (elem) elem.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
        }, 30);
    };
    const setFacetsDataInCollection = (facets) => {
        setFacetData(facets);
    };
    const loadProductFacetsV3 = (isFoodBrand, isFacetFiltersCountEnabled, filterFlag, isDesktopFacetsV3, isMobileFacetsV3, areDeliveryTypeFilterOptionsShown, abCategoryFilters) => (
        <MobileDesktopFacetsV3
            selectedFacet={facetsData}
            isFoodBrand={isFoodBrand}
            classes={classes}
            productFacetsBase={productFacetsBase}
            isFacetFiltersCountEnabled={isFacetFiltersCountEnabled}
            filterFlag={filterFlag}
            category={category}
            trackEventAction={trackEventAction}
            categoryData={wikiPageData}
            abCategorySortAndFilter={abCategorySortAndFilter}
            isDesktopFacetsV3={isDesktopFacetsV3}
            isMobileFacetsV3={isMobileFacetsV3}
            featureFlags={featureFlags}
            displayDeliveryMethod={areDeliveryTypeFilterOptionsShown && abCategoryFilters !== 'hide'}
            setFacetsDataInCollection={setFacetsDataInCollection}
            setFilterDataInCategoryBuilder={() => {}}
            presentation_family={presentation_family}
            openFacetDropdown=""
            setFacetLabel={() => {}}
            productCount={productCount}
            ssrDeviceType={ssrDeviceType}
            productDisplayLayout={() => {}}
            forPlantGuide
        />
    );
    const filterUI = () => {
        const isMobile = ssrDeviceType === 'mobile' || ssrDeviceType === 'tablet';
        const renderFacetUI = () => {
            if (isMobile) {
                return (
                    <Media
                        query="(max-width: 1024px)"
                        defaultMatches={ssrDeviceType !== 'desktop'}
                        render={() => loadProductFacetsV3(false, true, true, true, true, false)}
                    />
                );
            }
            return (
                <>
                    <Media
                        style={{ display: 'sticky' }}
                        query="(min-width: 1025px)"
                        defaultMatches={!isMobile}
                        render={() => loadProductFacetsV3(false, true, true, true, true, false)}
                    />
                    <PlantsFilterDialog forPlantGuide featureFlags={featureFlags} displayDeliveryMethod={displayDeliveryMethod} setFacetsOptions={setFacetsDataInCollection} setFilterOptions={() => {}} presentation_family={presentation_family} brand={wikiPageData?.brand} facetsData={facetsData} productFacets={productFacetsBase} setFilterDataInCategoryBuilder={() => {}} history={history} totalCount={productCount} ssrDeviceType={ssrDeviceType} />
                </>
            );
        };
        return (
            <>
                { renderFacetUI() }
            </>
        );
    };
    const [getProducts, {
        data,
    }] = useLazyQuery(CATEGORY_PRODUCT_QUERY, {
        variables: {
            productOptions: {
                page: 1,
                pageSize: 100,
                orderBy: 'BEST_SELLERS',
                facets: facetsData,
            },
            brand: 'plants',
            environment: GRAPHQL_ENV,
            id: category?.id,
            locale: 'en-us',
            zipCode: '',
            locationType: 'Residence',
        },
    });
    const isMounted = useRef(true); // see useEffect comment for explination of this useRef
    useEffect(() => {
        // useEffect normally runs on mount, unmount, and when any values given in the array param are modified
        // this query should not run on component mount. Only if one of the array values is modified
        // Setting a ref to false initiailly and only executing the query if the ref returns true
        // Ensures that the query will not run on mount when useEffect is first triggered
        if (isMounted.current) {
            // This query only runs if a filter prop is changed
            getProducts({
                variables: {
                    productOptions: {
                        page: 1,
                        pageSize: 100,
                        orderBy: 'BEST_SELLERS',
                        facets: facetsData,
                    },
                    brand: 'plants',
                    environment: GRAPHQL_ENV,
                    id: category?.id,
                    locale: 'en-us',
                    zipCode: '',
                    locationType: 'Residence',
                },
            });
        } else {
            isMounted.current = true;
        }
    }, [facetsData?.length]);
    useEffect(() => {
        window.addEventListener('resize', handleResize, false);
        setFacetData(selectedFacet);
        handleResize();
    }, []);
    let wikiProducts = data?.findCategoryById?.products || wikiPageData?.category?.products;
    wikiProducts = getWikiProducts(wikiProducts);
    let wikiProductsLength = wikiProducts.length;
    const wikiAlphabets = wikiProducts;
    const wikiProductsPending = false;
    const filteredItems = {};
    const checkForFilterItems = Boolean(filteredItems.length);
    if (searchText) {
        wikiProducts = wikiProducts.filter(({ product_name = '', alphabet = '' }) => product_name.toLowerCase().includes(searchText.toLowerCase()) || searchText[0].toLowerCase() === alphabet.toLowerCase());
        wikiProductsLength = wikiProducts.length;
    }
    return (
        <Container>
            <div className={`${classes.plantswikiStyles} plantsWikiWrapper`}>
                <div className={`${isSmallScreen ? 'd-block' : 'd-flex justify-content-around'} pb-2 pt-2`}>
                    <h1 aria-label="plants guide" className={`${isSmallScreen ? 'd-block' : 'w-50'} text-uppercase`}>Plant Guides</h1>
                    <div role="region" aria-label="search info" className={`${isSmallScreen ? 'd-block' : 'w-50'} headerContent`}>Search this library of plant care guides to learn more about the conditions, preferences, common issues, and information about each of your plants.</div>
                </div>
                <div className={`searchSection ${isSmallScreen ? 'd-block' : 'd-flex'} p-3`}>
                    <div className="inputSection p-3 m-0 justify-content-around form-group">
                        <SearchIcon className="searchIcon" />
                        <input type="search" name="searchText" id="searchText" placeholder="Enter plant name" className="p-2 form-control" value={searchText} autoComplete="off" onChange={onValueChange} />
                    </div>
                    <div role="region" aria-label="jump to section" className={`jumpToSection ${isSmallScreen ? 'd-block' : 'd-flex'}`}><span className={`${isSmallScreen ? 'd-block text-center' : 'd-flex'} p-2`}>Jump To:</span>
                        <div className="d-flex p-1">
                            {wikiAlphabets.map((ele) => {
                                let item = null;
                                if (ele.type === 'alphabet') {
                                    item = (
                                        <a href={`#${ele.alphabet}`} onClick={(e) => { e.preventDefault(); scrollToAplhabet(ele.alphabet); }} aria-label={ele.alphabet}>
                                            {ele.alphabet}
                                        </a>
                                    );
                                }
                                return item;
                            })}
                        </div>
                    </div>
                </div>
                {filterUI()}
                <Grid container className="wikiTiles pt-3">
                    {wikiProducts && wikiProductsLength === 0 ? (
                        <div className="text-center mt-4 noResults">{!wikiProductsPending && (searchText || checkForFilterItems) ? `Your ${checkForFilterItems ? 'applied filter(s)' : 'search' } ${ checkForFilterItems ? '' : `'${searchText}'` } did not return any results.` : null}</div>
                    ) : (
                        wikiProducts && wikiProducts.map((ele, index) => {
                            let item = null;
                            let oddOrEvenClass = '';
                            if (wikiProductsLength % 2 === 0 && wikiProductsLength === index + 1) {
                                oddOrEvenClass = ' align-wiki-tile';
                            } else if (wikiProductsLength === index + 1) {
                                oddOrEvenClass = ' odd-wiki-tile';
                            } else {
                                oddOrEvenClass = '';
                            }
                            if (ele.type === 'alphabet' && (!searchText || (searchText && ((wikiProducts[index + 1] && wikiProducts[index + 1].product_name && searchText[0].toLowerCase() === wikiProducts[index + 1].product_name[0].toLowerCase()) || searchText[0].toLowerCase() === ele.alphabet.toLowerCase())))) {
                                item =  (
                                    <Grid item xs={6} sm={4} md={4} lg={3} xl={3} className={`d-table pb-0 alphabetTile${oddOrEvenClass}`}>
                                        <a href={`#${ele.alphabet}`} className="alphabet d-table-cell align-middle justify-content-around pt-2 pt-md-3" id={ele.alphabet} aria-label={ele.alphabet} tabIndex="0">
                                            {ele.alphabet}
                                        </a>
                                    </Grid>
                                );
                            } else if (ele.type === 'product') {
                                item = (
                                    <Grid item xs={6} sm={4} md={4} lg={3} xl={3} className={`productTile${oddOrEvenClass}`}>
                                        <a href={getProductLink(ele.slug)} className="productTitle d-flex p-3 p-md-4 p-xl-5" aria-label={ele.product_name} tabIndex="0">
                                            <div>
                                                <div>
                                                    <picture>
                                                        {(ele.webp_collection_product_picture || ele.product_webp_picture) && <source srcSet={ele.webp_collection_product_picture ? ele.webp_collection_product_picture : ele.product_webp_picture} type="image/webp" height="100%" width="100%" alt={ele.product_name} />}
                                                        <img src={ele.collection_product_picture ? ele.collection_product_picture : ele.product_picture} height="100%" width="100%" alt={ele.product_name} />
                                                    </picture>
                                                </div>
                                                <div className="mt-1">{ele.product_name}</div>
                                            </div>
                                        </a>
                                    </Grid>
                                );
                            }
                            return item;
                        }))}
                </Grid>
            </div>
        </Container>
    );
};

PlantGuidePage.propTypes = {
    classes: object.isRequired,
    wikiPageData: object.isRequired,
    trackEventAction: func,
    abCategorySortAndFilter: string.isRequired,
    presentation_family: string.isRequired,
    history: object.isRequired,
    ssrDeviceType: string,
    featureFlags: object.isRequired,
    displayDeliveryMethod: bool.isRequired,
    selectedFacet: object.isRequired,
};

PlantGuidePage.defaultProps = {
    trackEventAction: () => {},
    ssrDeviceType: '',
};

const mapStateToProps = (state) => ({
    ssrDeviceType: getSSRDeviceType(state),
    featureFlags: getFeatureFlags(state),
    displayDeliveryMethod: getDisplayDeliveryMethod(state),
});

export default compose(
    withStyles(styles),
    withRouter,
    connect(mapStateToProps),
)(PlantGuidePage);
